import {
  type ParentTaskType,
  type SubTaskType,
  type TaskContext,
  type UserInfo,
  type hfb,
} from "@repo/api";
import {
  type TaskConfig,
  getAllDayConfig,
  getTimeInMinutes,
  isDueDateTask,
} from "@repo/ui";
import { assertUnreachable } from "@repo/utils";
import { addMinutes, differenceInMinutes } from "date-fns";
import { type ActionType, createAction, getType } from "typesafe-actions";

export type TaskModalMode = "CREATE" | "EDIT" | "VIEW";

type TaskModalLoadingState = "UPDATING" | "DELETING";

export type TaskModalContext = TaskContext;

export interface TaskModalState {
  id?: number;
  title?: string;
  defaultTitle?: boolean;
  description?: string;
  startDate?: Date;
  startTime?: Date;
  endDate?: Date;
  endTime?: Date;
  isDueDateTask?: boolean;
  dueDate?: Date;
  dueTime?: Date;
  parentTaskType?: ParentTaskType;
  subTaskType?: SubTaskType;
  allDay: boolean;
  isDone: boolean;
  mode: TaskModalMode;
  loadingState?: TaskModalLoadingState;
  selectedCoworkers?: UserInfo[];
  selectedTeam?: number;
  opportunityId?: number;
  service?: string;
  hfb?: hfb;
  cmpCode?: string;
  configs?: TaskConfig[];
  context: TaskModalContext;
}

export const TaskModalStateDefault: TaskModalState = {
  title: undefined,
  description: undefined,
  startDate: new Date(),
  startTime: new Date(),
  endDate: undefined,
  endTime: undefined,
  isDueDateTask: false,
  dueDate: new Date(),
  dueTime: undefined,
  allDay: false,
  parentTaskType: "task", // TODO: This needs to be changed when we add more types
  subTaskType: undefined,
  isDone: false,
  mode: "VIEW",
  loadingState: undefined,
  selectedCoworkers: undefined,
  selectedTeam: undefined,
  opportunityId: undefined,
  service: undefined,
  hfb: undefined,
  cmpCode: undefined,
  configs: undefined,
  context: "PROJECT",
};

export type TaskModalStateActions = typeof taskModalStateActions;
export const taskModalStateActions = {
  setTitle: createAction("SET_TITLE")<string>(),

  setDescription: createAction("SET_DESCRIPTION")<string>(),
  setStartDate: createAction("SET_START_DATE")<Date>(),
  setStartTime: createAction("SET_START_TIME")<Date | undefined>(),
  setEndDate: createAction("SET_END_DATE")<Date | undefined>(),
  setEndTime: createAction("SET_END_TIME")<Date | undefined>(),
  setIsDueTask: createAction("SET_IS_DUE_TASK")<boolean>(),
  setDueDate: createAction("SET_DUE_DATE")<Date | undefined>(),
  setDueTime: createAction("SET_DUE_TIME")<Date | undefined>(),
  setAllDay: createAction("SET_ALL_DAY")<boolean>(),
  setIsDone: createAction("SET_IS_DONE")<boolean>(),
  setParentTaskType: createAction("SET_PARENT_TASK_TYPE")<ParentTaskType>(),
  setSubTaskType: createAction("SET_SUB_TASK_TYPE")<{
    subTaskType: SubTaskType;
    title?: string;
  }>(),
  setMode: createAction("SET_MODE")<TaskModalMode>(),
  setLoadingState: createAction("SET_LOADING_STATE")<
    TaskModalLoadingState | undefined
  >(),
  setSelectedCoworkers: createAction("SET_SELECTED_COWORKERS")<
    UserInfo[] | undefined
  >(),
  setSelectedTeams: createAction("SET_SELECTED_TEAMS")<number | undefined>(),
  setOpportunityId: createAction("SET_OPPORTUNITY_ID")<number | undefined>(),
  setService: createAction("SET_SERVICE")<string | undefined>(),
  setHfb: createAction("SET_HFB")<hfb | undefined>(),
  setCmpCode: createAction("SET_CMP_CODE")<string | undefined>(),
  setConfigs: createAction("SET_CONFIGS")<TaskConfig[]>(),
};

export function TaskModalStateReducer(
  state: TaskModalState,
  action: ActionType<TaskModalStateActions>
) {
  switch (action.type) {
    case getType(taskModalStateActions.setTitle):
      return { ...state, title: action.payload };
    case getType(taskModalStateActions.setDescription):
      return { ...state, description: action.payload };
    case getType(taskModalStateActions.setStartDate): {
      let endDate = state.endDate;
      if (state?.startDate && state?.endDate) {
        const diff = differenceInMinutes(state.endDate, state.startDate);
        endDate = action.payload ? addMinutes(action.payload, diff) : undefined;
      }

      return { ...state, startDate: action.payload, endDate };
    }
    case getType(taskModalStateActions.setStartTime): {
      const timeInMinutes = getTimeInMinutes(
        state.parentTaskType,
        state.subTaskType,
        state.configs
      );
      let endTime = state.endTime;
      if (state?.startTime && state?.endTime) {
        const diff = differenceInMinutes(state.endTime, state.startTime);
        endTime = action.payload ? addMinutes(action.payload, diff) : undefined;
      } else if (action.payload) {
        if (timeInMinutes) {
          endTime = addMinutes(action.payload, timeInMinutes);
        }
      }

      return { ...state, startTime: action.payload, endTime };
    }
    case getType(taskModalStateActions.setEndDate):
      return { ...state, endDate: action.payload };
    case getType(taskModalStateActions.setEndTime):
      return { ...state, endTime: action.payload };
    case getType(taskModalStateActions.setDueDate):
      return { ...state, dueDate: action.payload };
    case getType(taskModalStateActions.setDueTime):
      return { ...state, dueTime: action.payload };
    case getType(taskModalStateActions.setIsDueTask):
      return {
        ...state,
        isDueDateTask: action.payload,
        dueDate: state.startDate,
        dueTime: state.startTime,
      };
    case getType(taskModalStateActions.setAllDay):
      return { ...state, allDay: action.payload };
    case getType(taskModalStateActions.setIsDone):
      return { ...state, isDone: action.payload };
    case getType(taskModalStateActions.setParentTaskType):
      return {
        ...state,
        parentTaskType: action.payload,
        subTaskType: undefined,
        title: undefined,
      };
    case getType(taskModalStateActions.setSubTaskType): {
      const allDay =
        getAllDayConfig(
          state.parentTaskType,
          action.payload.subTaskType,
          state.configs
        ) ?? false;

      const title =
        action.payload.title !== undefined ? action.payload.title : state.title;

      const timeInMinutes = getTimeInMinutes(
        state.parentTaskType,
        action.payload.subTaskType,
        state.configs
      );
      let startTime = state.startTime;
      let endTime = state.endTime;

      if (timeInMinutes) {
        if (!startTime) {
          startTime = new Date();
        }
        endTime = addMinutes(startTime, timeInMinutes);
      }

      const taskDates = isDueDateTask(
        state.parentTaskType,
        action.payload.subTaskType,
        state.configs
      )
        ? {
            isDueDateTask: true,
            dueDate: state.dueDate,
            dueTime: state.dueTime,
          }
        : {
            isDueDateTask: false,
            startTime,
            endTime,
          };

      return {
        ...state,
        subTaskType: action.payload.subTaskType,
        allDay,
        title,
        ...taskDates,
      };
    }
    case getType(taskModalStateActions.setSelectedCoworkers):
      return {
        ...state,
        selectedCoworkers: action.payload,
        selectedTeam: undefined,
      };
    case getType(taskModalStateActions.setSelectedTeams):
      return {
        ...state,
        selectedCoworkers: undefined,
        selectedTeam: action.payload,
      };
    case getType(taskModalStateActions.setOpportunityId):
      return { ...state, opportunityId: action.payload };
    case getType(taskModalStateActions.setMode):
      return { ...state, mode: action.payload };
    case getType(taskModalStateActions.setLoadingState):
      return { ...state, loadingState: action.payload };
    case getType(taskModalStateActions.setService):
      return { ...state, service: action.payload };
    case getType(taskModalStateActions.setHfb):
      return { ...state, hfb: action.payload };
    case getType(taskModalStateActions.setCmpCode):
      return { ...state, cmpCode: action.payload };
    case getType(taskModalStateActions.setConfigs):
      return { ...state, configs: action.payload };
  }
  assertUnreachable(action);
}
